import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip, Avatar,
  Divider, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Pagination, FormControl, InputLabel, Select, MenuItem,
  CircularProgress,
} from '@mui/material';
import { CheckCircle, Cancel, Description, Visibility, EventAvailable } from '@mui/icons-material';
import { LIST_PENDING_KYC_APPLICATIONS, UPDATE_DRIVER_KYC_STATUS, VERIFY_KYC_DOCUMENT } from '../graphql/queries';
import type { KYCApplication, KYCDocument } from '../types/kyc';

const client = generateClient();

// Document types that require an expiry date
const EXPIRY_REQUIRED = new Set(['DRIVERS_LICENSE', 'DRIVERS_LICENSE_BACK', 'VEHICLE_REGISTRATION', 'INSURANCE']);

const DOC_LABEL: Record<string, string> = {
  DRIVERS_LICENSE: 'License (Front)',
  DRIVERS_LICENSE_BACK: 'License (Back)',
  VEHICLE_REGISTRATION: 'Registration',
  INSURANCE: 'Insurance',
  PROFILE_PHOTO: 'Photo',
};

const EXPIRY_LABEL: Record<string, string> = {
  DRIVERS_LICENSE: 'License expiry',
  DRIVERS_LICENSE_BACK: 'License expiry',
  VEHICLE_REGISTRATION: 'Registration expiry',
  INSURANCE: 'Insurance expiry',
};

// Minimum allowed expiry date — 7 days from today
const MIN_EXPIRY = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
const TODAY = new Date().toISOString().split('T')[0];

export default function DriverApproval() {
  const [applications, setApplications] = useState<KYCApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [sortBy, setSortBy] = useState('newest');
  const [filterBy, setFilterBy] = useState('all');

  // Rejection dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<KYCApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Approval confirmation dialog
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  // Document viewer
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<KYCDocument | null>(null);

  // Expiry date dialog — shown when admin clicks Verify on an expirable document
  const [expiryDialogOpen, setExpiryDialogOpen] = useState(false);
  const [pendingVerify, setPendingVerify] = useState<{ app: KYCApplication; docType: string } | null>(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryError, setExpiryError] = useState('');
  const [processingDocs, setProcessingDocs] = useState<Set<string>>(new Set());

  useEffect(() => { fetchPendingApplications(); }, []);

  const fetchPendingApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const result: any = await client.graphql({
        query: LIST_PENDING_KYC_APPLICATIONS,
        variables: { limit: 100 },
      });
      setApplications(result.data.listKYCApplications.items);
    } catch (err: any) {
      setError(err.message || 'Failed to load pending applications');
    } finally {
      setLoading(false);
    }
  };

  // ── Verify document ──────────────────────────────────────────────────────────

  // Returns the app-level expiry date for a given document type
  const getExpiryForDoc = (app: KYCApplication, docType: string): string | undefined => {
    if (docType === 'DRIVERS_LICENSE' || docType === 'DRIVERS_LICENSE_BACK') return app.licenseExpiryDate;
    if (docType === 'VEHICLE_REGISTRATION') return app.registrationExpiryDate;
    if (docType === 'INSURANCE') return app.insuranceExpiryDate;
    return undefined;
  };

  const handleVerifyClick = (app: KYCApplication, docType: string, verified: boolean) => {
    if (verified) {
      // Un-verifying — no expiry needed, call directly
      submitVerify(app, docType, false, undefined);
      return;
    }
    if (EXPIRY_REQUIRED.has(docType)) {
      // Verifying an expirable doc — open expiry dialog first
      setPendingVerify({ app, docType });
      setExpiryDate('');
      setExpiryError('');
      setExpiryDialogOpen(true);
    } else {
      submitVerify(app, docType, true, undefined);
    }
  };

  const handleEditExpiry = (app: KYCApplication, docType: string) => {
    const existing = getExpiryForDoc(app, docType);
    setPendingVerify({ app, docType });
    setExpiryDate(existing ?? '');
    setExpiryError('');
    setExpiryDialogOpen(true);
  };

  const handleExpiryConfirm = () => {
    if (!expiryDate) { setExpiryError('Expiry date is required'); return; }
    if (expiryDate < MIN_EXPIRY) { setExpiryError('Expiry date must be at least 7 days from today'); return; }
    if (!pendingVerify) return;    setExpiryDialogOpen(false);
    // Always submit as verified=true — this dialog is used for both initial verify and expiry edits
    submitVerify(pendingVerify.app, pendingVerify.docType, true, expiryDate);
    setPendingVerify(null);
  };

  const submitVerify = async (
    app: KYCApplication,
    documentType: string,
    verified: boolean,
    expiry: string | undefined,
  ) => {
    const docKey = `${app.driverId}:${documentType}`;
    setProcessingDocs(prev => new Set(prev).add(docKey));
    try {
      const input: any = { driverId: app.driverId, documentType, verified, reviewerName: 'Admin User' };
      if (expiry) input.expiryDate = expiry;

      const result: any = await client.graphql({ query: VERIFY_KYC_DOCUMENT, variables: { input } });

      if (result.data.verifyKYCDocument.success) {
        await fetchPendingApplications();
      } else {
        setError(result.data.verifyKYCDocument.message || 'Verification failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify document');
    } finally {
      setProcessingDocs(prev => { const n = new Set(prev); n.delete(docKey); return n; });
    }
  };

  // ── Approve ──────────────────────────────────────────────────────────────────

  const handleApprove = (app: KYCApplication) => {
    if (!(app.licenseApproved && app.vehicleApproved && app.insuranceApproved)) {
      setError('License, vehicle registration, and insurance must all be verified before approval');
      return;
    }
    setSelectedApplication(app);
    setApproveDialogOpen(true);
  };

  const confirmApprove = async () => {
    if (!selectedApplication) return;
    try {
      setProcessing(true);
      await client.graphql({
        query: UPDATE_DRIVER_KYC_STATUS,
        variables: { input: { driverId: selectedApplication.driverId, kycStatus: 'APPROVED', reviewerName: 'Admin User' } },
      });
      setApplications(prev => prev.filter(a => a.driverId !== selectedApplication.driverId));
      setApproveDialogOpen(false);
      setSelectedApplication(null);
    } catch (err: any) {
      setError(err.message || 'Failed to approve application');
    } finally {
      setProcessing(false);
    }
  };

  // ── Reject ───────────────────────────────────────────────────────────────────

  const handleReject = async () => {
    if (!selectedApplication || !rejectionReason.trim()) return;
    try {
      setProcessing(true);
      await client.graphql({
        query: UPDATE_DRIVER_KYC_STATUS,
        variables: { input: { driverId: selectedApplication.driverId, kycStatus: 'REJECTED', rejectionReason, reviewerName: 'Admin User' } },
      });
      setApplications(prev => prev.filter(a => a.driverId !== selectedApplication.driverId));
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedApplication(null);
    } catch (err: any) {
      setError(err.message || 'Failed to reject application');
    } finally {
      setProcessing(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const formatExpiry = (dateStr?: string) =>
    dateStr ? new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null;

  const filteredApplications = applications
    .filter(app => {
      if (filterBy === 'complete') return app.documents.every(d => d.verified);
      if (filterBy === 'incomplete') return app.documents.some(d => !d.verified);
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.submittedAt || '').getTime() - new Date(a.submittedAt || '').getTime();
      if (sortBy === 'oldest') return new Date(a.submittedAt || '').getTime() - new Date(b.submittedAt || '').getTime();
      return 0;
    });

  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const paginatedApplications = filteredApplications.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Driver Onboarding Approval</Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Review and verify each document. An expiry date is required for license, registration, and insurance before you can verify them.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort by</InputLabel>
              <Select value={sortBy} onChange={e => setSortBy(e.target.value)} label="Sort by">
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Filter</InputLabel>
              <Select value={filterBy} onChange={e => setFilterBy(e.target.value)} label="Filter">
                <MenuItem value="all">All Applications</MenuItem>
                <MenuItem value="complete">Complete Docs</MenuItem>
                <MenuItem value="incomplete">Incomplete Docs</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="outlined" onClick={fetchPendingApplications}>Refresh</Button>
          </Box>
        </CardContent>
      </Card>

      {filteredApplications.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6">No Pending Applications</Typography>
            <Typography color="textSecondary">All driver applications have been processed.</Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={3}>
            {paginatedApplications.map(app => (
              <Grid item xs={12} md={6} key={app.driverId}>
                <Card elevation={2}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Chip label={new Date(app.submittedAt || '').toLocaleDateString()} size="small" variant="outlined" />
                    </Box>

                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar sx={{ width: 56, height: 56 }}>{app.driverId.charAt(0).toUpperCase()}</Avatar>
                      <Box>
                        <Typography variant="h6">Driver ID: {app.driverId.substring(0, 8)}...</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Applied: {new Date(app.submittedAt || '').toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {app.vehicleInfo && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>Vehicle</Typography>
                        <Typography variant="body2">
                          {app.vehicleInfo.make} {app.vehicleInfo.model} ({app.vehicleInfo.year})
                        </Typography>
                        <Typography variant="body2" color="textSecondary">Plate: {app.vehicleInfo.licensePlate}</Typography>
                        {app.licenseNumber && (
                          <Typography variant="body2" color="textSecondary">License #: {app.licenseNumber}</Typography>
                        )}
                      </Box>
                    )}

                    {/* Expiry date summary */}
                    {(app.licenseExpiryDate || app.registrationExpiryDate || app.insuranceExpiryDate) && (
                      <Box mb={2} p={1.5} sx={{ bgcolor: 'success.light', borderRadius: 1 }}>
                        <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                          <EventAvailable fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                          Expiry dates on file
                        </Typography>
                        {app.licenseExpiryDate && (
                          <Typography variant="caption" display="block">License: {formatExpiry(app.licenseExpiryDate)}</Typography>
                        )}
                        {app.registrationExpiryDate && (
                          <Typography variant="caption" display="block">Registration: {formatExpiry(app.registrationExpiryDate)}</Typography>
                        )}
                        {app.insuranceExpiryDate && (
                          <Typography variant="caption" display="block">Insurance: {formatExpiry(app.insuranceExpiryDate)}</Typography>
                        )}
                      </Box>
                    )}

                    {/* Documents */}
                    <Typography variant="subtitle2" gutterBottom>Documents ({app.documents.length})</Typography>
                    <Box display="flex" flexDirection="column" gap={1} mb={2}>
                      {app.documents.map((doc, idx) => {
                        const docKey = `${app.driverId}:${doc.type}`;
                        const busy = processingDocs.has(docKey);
                        const needsExpiry = EXPIRY_REQUIRED.has(doc.type);
                        const expiryDate = getExpiryForDoc(app, doc.type);
                        return (
                          <Box key={idx} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Chip
                              label={DOC_LABEL[doc.type] ?? doc.type}
                              color={doc.verified ? 'success' : 'warning'}
                              variant={doc.verified ? 'filled' : 'outlined'}
                              size="small"
                              icon={doc.viewUrl ? <Visibility /> : <Description />}
                              onClick={() => doc.viewUrl && (setSelectedDocument(doc), setDocumentViewerOpen(true))}
                              sx={{ cursor: doc.viewUrl ? 'pointer' : 'default', minWidth: 130 }}
                            />
                            {doc.verified ? (
                              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                <Chip
                                  label="Verified"
                                  color="success"
                                  size="small"
                                  icon={<CheckCircle />}
                                  onDelete={() => handleVerifyClick(app, doc.type, true)}
                                  deleteIcon={<Cancel />}
                                  disabled={busy}
                                />
                                {needsExpiry && (
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    {expiryDate ? (
                                      <Typography variant="caption" color="textSecondary">
                                        exp. {formatExpiry(expiryDate)}
                                      </Typography>
                                    ) : (
                                      <Typography variant="caption" color="error">
                                        No expiry set
                                      </Typography>
                                    )}
                                    <Button
                                      size="small"
                                      variant="text"
                                      sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                                      onClick={() => handleEditExpiry(app, doc.type)}
                                      disabled={busy}
                                    >
                                      {expiryDate ? 'Edit' : 'Set Expiry'}
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                startIcon={busy ? <CircularProgress size={14} /> : <CheckCircle />}
                                onClick={() => handleVerifyClick(app, doc.type, false)}
                                disabled={busy || !doc.viewUrl}
                              >
                                {needsExpiry ? 'Verify + Expiry' : 'Verify'}
                              </Button>
                            )}
                          </Box>
                        );
                      })}
                    </Box>

                    <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                      Click document chip to view • Expiry date required for license, registration, and insurance
                    </Typography>

                    <Box display="flex" gap={2}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleApprove(app)}
                        fullWidth
                        disabled={processing || !(app.licenseApproved && app.vehicleApproved && app.insuranceApproved)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => { setSelectedApplication(app); setRejectDialogOpen(true); }}
                        fullWidth
                        disabled={processing}
                      >
                        Reject
                      </Button>
                    </Box>
                    {!(app.licenseApproved && app.vehicleApproved && app.insuranceApproved) && (
                      <Typography variant="caption" color="error" display="block" textAlign="center" mt={1}>
                        Verify license (front + back), registration, and insurance to enable approval
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" showFirstButton showLastButton />
            </Box>
          )}
        </>
      )}

      {/* Expiry Date Dialog */}
      <Dialog open={expiryDialogOpen} onClose={() => setExpiryDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {pendingVerify && getExpiryForDoc(pendingVerify.app, pendingVerify.docType)
            ? 'Update Expiry Date'
            : 'Enter Expiry Date'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {pendingVerify ? EXPIRY_LABEL[pendingVerify.docType] ?? 'Document expiry' : ''}
          </Typography>
          <TextField
            fullWidth
            type="date"
            label="Expiry date"
            value={expiryDate}
            onChange={e => { setExpiryDate(e.target.value); setExpiryError(''); }}
            inputProps={{ min: MIN_EXPIRY }}
            InputLabelProps={{ shrink: true }}
            error={!!expiryError}
            helperText={expiryError || 'Must be at least 7 days from today'}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpiryDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleExpiryConfirm} disabled={!expiryDate}>
            Confirm & Verify
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => !processing && setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Driver Application</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Please provide a reason for rejecting this application:
          </Typography>
          <TextField
            fullWidth multiline rows={4}
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            placeholder="Enter rejection reason..."
            sx={{ mt: 2 }}
            disabled={processing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button onClick={handleReject} color="error" disabled={!rejectionReason.trim() || processing}>
            {processing ? <CircularProgress size={24} /> : 'Reject Application'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => !processing && setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Driver Application</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>All documents have been verified</Alert>
          {selectedApplication && (
            <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2">Driver ID:</Typography>
              <Typography variant="body2" gutterBottom>{selectedApplication.driverId}</Typography>
              {selectedApplication.vehicleInfo && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Vehicle:</Typography>
                  <Typography variant="body2">
                    {selectedApplication.vehicleInfo.make} {selectedApplication.vehicleInfo.model} ({selectedApplication.vehicleInfo.year})
                  </Typography>
                </>
              )}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Expiry dates:</Typography>
              <Typography variant="body2">License: {formatExpiry(selectedApplication.licenseExpiryDate) ?? '—'}</Typography>
              <Typography variant="body2">Registration: {formatExpiry(selectedApplication.registrationExpiryDate) ?? '—'}</Typography>
              <Typography variant="body2">Insurance: {formatExpiry(selectedApplication.insuranceExpiryDate) ?? '—'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button onClick={confirmApprove} color="success" variant="contained" disabled={processing}>
            {processing ? <CircularProgress size={24} /> : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Viewer */}
      <Dialog open={documentViewerOpen} onClose={() => setDocumentViewerOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{selectedDocument && (DOC_LABEL[selectedDocument.type] ?? selectedDocument.type)}</Typography>
            <Button variant="outlined" size="small" onClick={() => selectedDocument?.viewUrl && window.open(selectedDocument.viewUrl, '_blank')}>
              Open in New Tab
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {selectedDocument?.viewUrl && (
            <Box component="iframe" src={selectedDocument.viewUrl} sx={{ width: '100%', height: '100%', border: 'none', flexGrow: 1 }} title="Document" />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentViewerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
